import { money } from "@/lib/api";
import { format } from "date-fns";

export function Invoice({ order, restaurant }) {
  const r = restaurant || order.restaurant || {};
  const gst = order.gst_snapshot || {};
  const showTax = gst.enabled && gst.show_on_invoice !== false;
  return (
    <div className="sm-invoice bg-white p-6 text-sm text-slate-700" data-testid="invoice">
      <div className="flex items-start justify-between border-b border-slate-200 pb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900">{r.name}</h2>
          {r.address && <p className="text-xs text-slate-500">{r.address}</p>}
          {r.phone && <p className="text-xs text-slate-500">Ph: {r.phone}</p>}
          {showTax && r.gstin && <p className="text-xs text-slate-500">GSTIN: {r.gstin}</p>}
        </div>
        <div className="text-right">
          <p className="font-heading text-base font-semibold text-slate-900">{showTax ? "Tax Invoice" : "Invoice"}</p>
          <p className="text-xs" data-testid="invoice-number">{order.invoice_number}</p>
          <p className="text-xs">Order {order.order_number}</p>
          <p className="text-xs">{format(new Date(order.created_at), "dd MMM yyyy, hh:mm a")}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 py-3 text-xs text-slate-500">
        {order.table_name && <span>Table: <b className="text-slate-700">{order.table_name}</b></span>}
        {order.customer?.name && <span>Customer: <b className="text-slate-700">{order.customer.name}</b></span>}
        {order.customer?.phone && <span>Phone: {order.customer.phone}</span>}
        <span>Payment: <b className="capitalize text-slate-700">{order.payment_status}</b>{order.payment_method ? ` (${order.payment_method})` : ""}</span>
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="border-y border-slate-200 text-xs uppercase text-slate-400">
            <th className="py-2">Item</th><th className="py-2 text-center">Qty</th>
            <th className="py-2 text-right">Rate</th><th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <tr key={i} className="border-b border-slate-100 align-top">
              <td className="py-2">
                <p className="font-medium text-slate-800">{it.name}</p>
                {[...(it.variations || []), ...(it.addons || [])].map((v, j) => (
                  <p key={j} className="text-xs text-slate-500">{v.group}: {v.option}{v.price ? ` (${v.mode === "fixed" ? "" : "+"}${money(v.price)})` : ""}</p>
                ))}
                {showTax && it.tax_rate > 0 && (
                  <p className="text-[11px] text-slate-400">GST {it.tax_rate}% {it.tax_type}: {money(it.tax_amount)}</p>
                )}
              </td>
              <td className="py-2 text-center">{it.quantity}</td>
              <td className="py-2 text-right">{money(it.unit_price)}</td>
              <td className="py-2 text-right">{money(it.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="ml-auto mt-3 w-full max-w-xs space-y-1">
        <Row label="Subtotal" value={money(order.subtotal)} />
        {showTax && order.tax_total > 0 && (
          <>
            <Row label="CGST" value={money(Math.round(order.tax_total * 50) / 100)} muted />
            <Row label="SGST" value={money(order.tax_total - Math.round(order.tax_total * 50) / 100)} muted />
            <Row label={gst.inclusive ? "GST (included)" : "GST total"} value={money(order.tax_total)} />
          </>
        )}
        <div className="flex justify-between border-t border-slate-200 pt-2 font-heading text-base font-bold text-slate-900">
          <span>Grand Total</span><span data-testid="invoice-total">{money(order.grand_total)}</span>
        </div>
      </div>
      {order.customer?.note && <p className="mt-4 text-xs text-slate-500">Note: {order.customer.note}</p>}
      <p className="mt-6 text-center text-xs text-slate-400">Thank you for dining with us!</p>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "text-xs text-slate-400" : ""}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

export function whatsappShareText(order, restaurant) {
  const r = restaurant || order.restaurant || {};
  const lines = order.items.map((i) => `• ${i.quantity} x ${i.name}${[...(i.variations || []), ...(i.addons || [])].length ? ` (${[...(i.variations || []), ...(i.addons || [])].map((v) => v.option).join(", ")})` : ""} — ${money(i.line_total)}`);
  return [
    `*${r.name}* — Order ${order.order_number}`,
    order.table_name ? `Table: ${order.table_name}` : "",
    ...lines,
    `Subtotal: ${money(order.subtotal)}`,
    order.tax_total > 0 ? `GST: ${money(order.tax_total)}` : "",
    `*Total: ${money(order.grand_total)}*`,
    `Invoice: ${order.invoice_number}`,
  ].filter(Boolean).join("\n");
}

export function waLink(text, number = "") {
  const n = (number || "").replace(/\D/g, "");
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
}
